import React, { useState } from "react";
import axios from "axios";
import { CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { MaskReveal, FadeUp } from "@/components/Motion";
import { Eyebrow } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paste your published Google Form embed URL here to swap the native form for the Google Form.
const GOOGLE_FORM_EMBED_URL = "https://docs.google.com/forms/d/e/1FAIpQLSePuWtoJpnn83w2pm2h7H445EdXuf3Ney4TC451A-D3W8M7xg/viewform?embedded=true";

export default function Waitlist() {
  const [form, setForm] = useState({ name: "", email: "", role: "student" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/waitlist`, form);
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div data-testid="waitlist-page">
      <section className="relative overflow-hidden bg-white pt-28 sm:pt-36 pb-16 sm:pb-24 min-h-[70vh]">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="absolute -top-16 right-0 h-72 w-72 rounded-full bg-ol-blue/15 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-2xl px-5 sm:px-8">
          <FadeUp className="text-center">
            <div className="flex justify-center"><Eyebrow>Early access</Eyebrow></div>
            <h1 className="mt-6 text-[2.2rem] sm:text-5xl font-extrabold tracking-tight text-ol-ink leading-[1.04]">
              <MaskReveal lines={["Join the OneLease"]} delay={0.1} />
              <MaskReveal lines={["Waitlist."]} delay={0.24} lineClass="text-ol-blue" />
            </h1>
            <p className="mt-5 text-lg text-ol-gray leading-relaxed">
              Be first in line when OneLease launches. Drop your details and we'll invite you to
              create your profile the moment we go live.
            </p>
          </FadeUp>

          <FadeUp delay={0.15} className="mt-10">
            {GOOGLE_FORM_EMBED_URL ? (
              <div className="rounded-[2rem] bg-white p-2 soft-shadow overflow-hidden">
                <iframe
                  title="OneLease Waitlist Form"
                  src={GOOGLE_FORM_EMBED_URL}
                  className="w-full rounded-[1.6rem]"
                  height={720}
                  data-testid="waitlist-google-form"
                >
                  Loading…
                </iframe>
              </div>
            ) : status === "done" ? (
              <div className="rounded-[2rem] bg-white p-10 text-center soft-shadow" data-testid="waitlist-success">
                <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-ol-blue/10 text-ol-blue">
                  <CheckCircle size={34} weight="fill" />
                </div>
                <h2 className="mt-5 text-2xl font-bold text-ol-ink">You're on the list!</h2>
                <p className="mt-2 text-ol-gray">We'll be in touch soon at {form.email}.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-[2rem] bg-white p-6 sm:p-9 soft-shadow" data-testid="waitlist-form">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-ol-ink mb-2">Full name</label>
                    <input
                      required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="waitlist-name"
                      className="w-full rounded-2xl border border-gray-200 bg-ol-surface px-5 py-4 text-ol-ink outline-none focus:border-ol-blue focus:ring-2 focus:ring-ol-blue/20 transition-colors"
                      placeholder="Jordan Rivera"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-ol-ink mb-2">Email</label>
                    <input
                      required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="waitlist-email"
                      className="w-full rounded-2xl border border-gray-200 bg-ol-surface px-5 py-4 text-ol-ink outline-none focus:border-ol-blue focus:ring-2 focus:ring-ol-blue/20 transition-colors"
                      placeholder="you@university.edu"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-ol-ink mb-2">I am a…</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[["student", "Student"], ["parent", "Parent"], ["manager", "Manager"]].map(([val, label]) => (
                        <button
                          type="button" key={val}
                          onClick={() => setForm({ ...form, role: val })}
                          data-testid={`waitlist-role-${val}`}
                          className={`rounded-2xl px-3 py-3.5 text-sm font-semibold transition-colors duration-200 ${
                            form.role === val ? "bg-ol-blue text-white" : "bg-ol-surface text-ol-ink hover:bg-ol-blue/10"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    type="submit" disabled={status === "loading"}
                    data-testid="waitlist-submit"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-ol-blue px-7 py-4 text-base font-semibold text-white shadow-[0_16px_40px_-12px_rgba(37,99,235,0.7)] hover:bg-ol-deep transition-colors duration-200 disabled:opacity-60"
                  >
                    {status === "loading" ? "Joining…" : "Join the Waitlist"}
                    <PaperPlaneTilt size={18} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                  {status === "error" && (
                    <p className="text-center text-sm text-red-500" data-testid="waitlist-error">Something went wrong. Please try again.</p>
                  )}
                </div>
                <p className="mt-4 text-center text-xs text-ol-gray">
                  A Google Form embed can be dropped in here anytime, submissions will still flow to your sheet.
                </p>
              </form>
            )}
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
